import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNotifications } from '@/hooks/useNotifications';
import { usePortalBase } from '@/hooks/usePortalBase';
import { notificationLinkFor } from '@/lib/notificationLink';
import { formatDistanceToNow } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import {
  AlertTriangle,
  Bell,
  Calendar,
  CheckCircle2,
  DollarSign,
  FileText,
  Info,
  Loader2,
  Users,
  Wrench,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function NotificationsMenu() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const portalBase = usePortalBase();
  const { notifications, unreadCount, isLoading, markAsRead, markAllAsRead } =
    useNotifications();
  const [isOpen, setIsOpen] = useState(false);

  const getIcon = (category: string, type: string) => {
    if (type === 'warning')
      return <AlertTriangle className="h-4 w-4 text-warning" />;
    if (type === 'error')
      return <AlertTriangle className="h-4 w-4 text-destructive" />;

    switch (category) {
      case 'payment':
        return <DollarSign className="h-4 w-4 text-primary" />;
      case 'contract':
        return <Calendar className="h-4 w-4 text-primary" />;
      case 'maintenance':
        return <Wrench className="h-4 w-4 text-warning" />;
      case 'task':
        return <FileText className="h-4 w-4 text-primary" />;
      case 'invitation':
        return <Users className="h-4 w-4 text-primary" />;
      default:
        return <Info className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const handleNotificationClick = (notification: (typeof notifications)[0]) => {
    if (!notification.is_read) {
      markAsRead.mutate(notification.id);
    }
    setIsOpen(false);

    if (notification.link) {
      // Absolute URLs (e.g. a public payment link) open in a new tab;
      // relative SPA paths route in-app.
      if (/^https?:\/\//i.test(notification.link)) {
        window.open(notification.link, '_blank', 'noopener,noreferrer');
      } else {
        navigate(notificationLinkFor(notification.link, portalBase));
      }
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  // Show only first 10 unread notifications in dropdown
  const displayedNotifications = notifications.slice(0, 10);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -end-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-background z-50">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="font-semibold">{t('الإشعارات', 'Notifications')}</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={handleMarkAllAsRead}
              disabled={markAllAsRead.isPending}
            >
              <CheckCircle2 className="h-3 w-3 me-1" />
              {t('تحديد الكل كمقروء', 'Mark all as read')}
            </Button>
          )}
        </div>

        <ScrollArea className="h-[300px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : displayedNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Bell className="h-8 w-8 mb-2" />
              <p className="text-sm">
                {t('لا توجد إشعارات', 'No notifications')}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {displayedNotifications.map((notification) => (
                <button
                  type="button"
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full text-start px-4 py-3 hover:bg-muted/50 transition-colors ${
                    !notification.is_read ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="mt-0.5">
                      {getIcon(notification.category, notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm ${
                          !notification.is_read ? 'font-medium' : ''
                        }`}
                      >
                        {notification.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(
                          new Date(notification.created_at),
                          {
                            addSuffix: true,
                            locale: language === 'ar' ? ar : enUS,
                          }
                        )}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>

        {notifications.length > 0 && (
          <div className="border-t p-2">
            <Button
              variant="ghost"
              className="w-full justify-center text-sm"
              onClick={() => {
                setIsOpen(false);
                navigate(`${portalBase}/notifications`);
              }}
            >
              {t('عرض جميع الإشعارات', 'View All Notifications')}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
